import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useEffect } from 'react'
import useEnhancedFormik from '../../hooks/useEnhancedFormik'
import { STEPS } from '../../common/constants'
import { shippingSend4ServicesPropTypes } from '../../common/propTypes'
import { nullablePropType } from '../../common/propTypes'
import {
  makeFieldErrorsPropType,
  makeValuesPropType,
} from '../../common/formikPropTypes'
import { generateTouchedObject } from '../../libs/formik'

import {
  ERROR_FIELD_MIN_NUMBER_MONETARY_AMOUNT,
  ERROR_FIELD_MAX_NUMBER_MONETARY_AMOUNT,
} from '../../libs/formErrors'
import ConfirmationWithErrorModal from '../../ui/modals/confirmationWithError'
import ActionButtons from '../../ui/views/shippingSend/ActionButtons'
import ShippingSendForm from '../../ui/views/shippingSend/FormWrapper'
import Step4AdditServicesUI from '../../ui/views/shippingSend/Step4AdditServices'

import {
  MAX_DECLARED_VALUE_ALLOWED,
  MAX_REFUND_AMOUND_ALLOWED,
  MAX_INSURANCE_VALUE_ALLOWED,
} from './step4constants'

import useShippingSendStep from './useShippingSendStep'

const ServicesSchema = Yup.object().shape({
  declaredValue: Yup.number()
    .nullable()
    //.required()
    .min(0)
    .max(MAX_DECLARED_VALUE_ALLOWED),
  withRefund: Yup.boolean(),
  refundAmount: Yup.number()
    .nullable()
    .when('withRefund', {
      is: true,
      then: Yup.number()
        .nullable()
        .min(0, ERROR_FIELD_MIN_NUMBER_MONETARY_AMOUNT)
        .max(MAX_REFUND_AMOUND_ALLOWED, ERROR_FIELD_MAX_NUMBER_MONETARY_AMOUNT)
        .required(),
    }),
  insuranceMerchandise: Yup.boolean(),
  insuranceValue: Yup.number()
    .nullable()
    .when('insuranceMerchandise', {
      is: true,
      then: Yup.number()
        .nullable()
        .min(0, ERROR_FIELD_MIN_NUMBER_MONETARY_AMOUNT)
        .max(
          MAX_INSURANCE_VALUE_ALLOWED,
          ERROR_FIELD_MAX_NUMBER_MONETARY_AMOUNT,
        )
        .required(),
    }),
  withReturn: Yup.boolean(),
  returnWithHandling: Yup.boolean(),
  item2: Yup.boolean(),
  scanClientSlip: Yup.boolean(),
  item3: Yup.boolean(),
  verifyCode: Yup.boolean(),
})

// TODO: Still pending definition
const INITIAL_VALUES = {
  declaredValue: null,
  withRefund: false,
  refundAmount: null,
  insuranceMerchandise: false,
  insuranceValue: null,
  withReturn: false, // con retorno
  returnWithHandling: false, // retorno con manipulación
  item2: false,
  scanClientSlip: false,
  item3: false,
  verifyCode: false, // comprobación QR, BIDI, etc.
}

const Step4AdditServices = ({
  savedValues,
  activeStep,
  serverFieldErrors,
  hasOtherError,
  onSubmitSuccess,
  onCancel,
  onSetDirty,
  hasConfirmation,
  onCancelConfirmation,
  onAcceptConfirmation,
  international,
  isPudo,
}) => {
  const formik = useFormik({
    validationSchema: ServicesSchema,
    initialValues: savedValues ?? INITIAL_VALUES,
    initialTouched: !!savedValues
      ? generateTouchedObject(shippingSend4ServicesPropTypes, savedValues)
      : undefined,
    onSubmit: onSubmitSuccess,
  })

  const enhancedFormik = useEnhancedFormik(
    shippingSend4ServicesPropTypes,
    formik,
    { fieldErrors: serverFieldErrors },
  )

  const { handleDiscardConfirmation, hasServerError, hasAnyError } =
    useShippingSendStep({
      formik,
      enhancedFormik,
      onSetDirty,
      hasConfirmation,
      onAcceptConfirmation,
      serverFieldErrors,
      propTypes: shippingSend4ServicesPropTypes,
    })

    useEffect(()=>{
      if(hasConfirmation && !hasAnyError)
        onSubmitSuccess(formik.values)
    },[formik.values, hasAnyError, hasConfirmation, onSubmitSuccess])
    

  return (
    <ShippingSendForm onSubmit={enhancedFormik.handleSubmit}>
      <Step4AdditServicesUI
        international={international}
        isPudo={isPudo}
        values={formik.values}
        touched={formik.touched}
        fieldErrors={enhancedFormik.fieldErrors}
        formErrors={enhancedFormik.formErrors}
        onChange={enhancedFormik.handleChange}
        onBlur={formik.handleBlur}
      />

      <ActionButtons
        isDirty={formik.dirty}
        isSaved={savedValues !== null}
        onCancel={onCancel}
        hasOtherError={hasOtherError}
        hasServerError={hasServerError}
        currentStep={STEPS.SERVICES}
        activeStep={activeStep}
      />

      {hasConfirmation && hasAnyError ? (
        <ConfirmationWithErrorModal
          onDiscard={handleDiscardConfirmation}
          onAmend={onCancelConfirmation}
        />
      ) : null}
    </ShippingSendForm>
  )
}

Step4AdditServices.propTypes = {
  savedValues: nullablePropType(
    makeValuesPropType(shippingSend4ServicesPropTypes).isRequired,
  ),
  activeStep: PropTypes.oneOf(Object.values(STEPS)).isRequired,
  serverFieldErrors: nullablePropType(
    makeFieldErrorsPropType(shippingSend4ServicesPropTypes).isRequired,
  ),
  hasOtherError: PropTypes.bool.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSetDirty: PropTypes.func.isRequired,
  hasConfirmation: PropTypes.bool.isRequired,
  onCancelConfirmation: PropTypes.func.isRequired,
  onAcceptConfirmation: PropTypes.func.isRequired,
  international: PropTypes.bool.isRequired,
  isPudo: PropTypes.bool.isRequired,
}

export default Step4AdditServices
